<template>
  <div class="wt-asset-editor-layout-general" :data-source="openedFrom">
    <div class="wt-asset-editor-header" :style="headerStyle">
      <transition name="ae-fade">
        <slot name="header" />
      </transition>
    </div>

    <div class="wt-editor-view-inner-scroll" :style="mainStyle" ref="scroll">
      <div class="wt-editor-view-inner" @scroll.passive="closeMCE">
        <div class="-top-right-floating wt2-sg">
          <slot name="top-right-floating">
            <div
              v-if="showTranscriptForEffectPage"
              class="wt2-color-link wt2-text-n"
              v-text="toggleTranscriptLabel"
              @click="$_onToggleTranscript"
            />
          </slot>
        </div>

        <div class="wt-asset-left-bar">
          <slot name="left" />
        </div>

        <div class="wt-asset-editor-middle" :class="middleClassObj">
          <transcript-layer
            :toggle="toggleTranscript"
            :marginTop="49"
            :arrowRight="68"
          />
          <div class="wt-editor-top-bar">
            <slot name="top" />
          </div>
          <div class="wt-asset-editor-main">
            <slot name="main" />
          </div>
          <div v-if="hasLoader" class="wt-asset-editor-loader">
            <slot name="loader" />
          </div>
          <div class="wt-editor-bottom-bar">
            <slot name="bottom" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasPreFooter" class="-pre-footer">
      <transition name="ae-fade">
        <slot name="pre-footer">Scenes</slot>
      </transition>
    </div>

    <transition name="ae-fade">
      <slot name="footer" />
    </transition>
  </div>
</template>

<script>
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { mapState } from 'vuex';
import TranscriptMixin from 'AssetEditor/functionality/Info/TranscriptMixin';

export default {
  mixins: [TranscriptMixin],

  props: {
    headerHeight: { required: false, default: 80 },
    footerHeight: { required: false, default: 50 },
    preFooterHeight: { required: false, default: 174 },
    scalable: { required: false, default: false },
  },

  mounted() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        bus.$emit('layout-resize', { width, height });
      }
    });
    this.resizeObserver.observe(this.$refs.scroll);
    const { clientWidth: width, clientHeight: height } = this.$refs.scroll;
    bus.$emit('layout-resize', { width, height });
  },

  beforeDestroy() {
    this.resizeObserver.disconnect();
    this.resizeObserver = null;
  },

  computed: {
    ...mapState('assetEditor', [
      'openedFrom',
      'sceneDetectionEnabled',
      'workModel',
    ]),

    hasPreFooter() {
      return !!this.$slots['pre-footer'];
    },

    hasLoader() {
      return !!this.$slots['loader'];
    },

    headerStyle() {
      return {
        height: `${this.headerHeight}px`,
      };
    },

    mainStyle() {
      const delta =
        this.headerHeight +
        this.footerHeight +
        (this.hasPreFooter ? this.preFooterHeight : 0);
      return {
        height: `calc(100% - ${delta}px)`,
      };
    },

    middleClassObj() {
      const isVideo =
        app.assetType.isVIDEO(this.workModel) ||
        app.assetType.isFRAGMENT(this.workModel) ||
        app.assetType.isSOCIALVIDEO(this.workModel);

      return {
        '-no-overflow': this.toggleTranscript,
        'scene-detect-on': this.sceneDetectionEnabled,
        'is-video': isVideo,
      };
    },
  },

  methods: {
    closeMCE() {
      document
        .querySelectorAll('body >.mce-container.mce-panel')
        .forEach((elm) => {
          if (elm.style.display !== 'none') {
            elm.style.display = 'none';
          }
        });
    },

    moreInfo() {
      bus.$emit('moreInfo');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'asset_editor/_variables.scss';

.wt-asset-editor-layout-general {
  height: 100%;
  display: flex;
  flex-flow: column;

  .wt-editor-view-inner-scroll {
    overflow: auto;
    transition: height 1s;

    .wt-editor-view-inner {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      //overflow-y: auto;
      min-width: min-content;
      position: relative;
      background-color: rgba(0, 0, 0, 0.07);

      .-top-right-floating {
        position: absolute;
        top: 20px;
        right: 30px;
        z-index: 1;
      }

      .wt-asset-left-bar,
      .wt-asset-editor-middle,
      .wt-asset-top-bar {
        margin: auto;
      }

      .wt-asset-editor-main {
        z-index: 1;
      }

      .wt-asset-left-bar {
        background-color: #f7f7f7;
        width: 480px;
        height: 100%;
        overflow: auto;
        overflow: overlay; // doesn't work on Firefox
        z-index: 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;

        > div {
          padding-top: 40px;
          padding-bottom: 0;
          flex-grow: 1;
        }
      }

      .wt-asset-editor-middle {
        overflow: auto;
        overflow: overlay; // doesn't work on Firefox
        height: 100%;
        display: grid;
        flex: 1;
        position: relative;

        &.-no-overflow {
          overflow: hidden;
        }

        .wt-editor-top-bar {
          align-self: end;
          grid-row: 1 / 2;
          grid-column: 2 / 3;
        }

        .wt-asset-editor-main,
        .wt-asset-editor-loader {
          grid-row: 2 / 3;
          grid-column: 2 / 3;
        }

        .wt-asset-editor-loader {
          pointer-events: none;
          z-index: 2;
        }

        .wt-editor-bottom-bar {
          border: 1px solid transparent;
          grid-row: 3 / 4;
          grid-column: 2 / 3;
          height: 100%;
        }
      }
    }
  }

  .-pre-footer {
    height: 180px;
    border-top: #d9d9d9 1px solid;
  }

  $break-point-900: 900px;
  $break-point-740: 740px;
  $break-point-690: 690px;

  .wt-asset-editor-middle {
    --aml-player-tophead: 80px;
    --aml-player-width: 688px;
    --aml-player-height: 387px;
    //background: rgba(deepskyblue, 0.2);
  }

  @media screen and (min-height: $break-point-740 + 1) and (max-height: $break-point-900 - 1) {
    .wt-asset-editor-middle {
      --aml-player-tophead: 30px;
      //background: rgba(green, 0.2);

      &.is-video {
        --aml-player-width: 688px;
        --aml-player-height: 387px;

        &.scene-detect-on {
          --aml-player-width: 608px;
          --aml-player-height: 342px;
        }
      }
    }
  }

  @media screen and (min-height: $break-point-690) and (max-height: $break-point-740) {
    .wt-asset-editor-middle {
      --aml-player-tophead: 30px;
      //background: rgba(orange, 0.2);

      &.is-video {
        --aml-player-width: 608px;
        --aml-player-height: 342px;
      }
    }
  }

  @media screen and (max-height: $break-point-690 - 1) {
    .wt-asset-editor-middle {
      --aml-player-tophead: 30px;
      //background: rgba(red, 0.2);

      &.is-video {
        --aml-player-tophead: 10px;
        --aml-player-width: 528px;
        --aml-player-height: 297px;
      }
    }
  }

  &[data-source='asset-gallery'] .wt-asset-editor-middle,
  &[data-source='timeline-change-segment'] .wt-asset-editor-middle,
  &[data-source='original-video'] .wt-asset-editor-middle {
    grid-template-columns:
      minmax(20px, auto) minmax(auto, var(--aml-player-width))
      minmax(20px, auto);
    grid-template-rows:
      var(--aml-player-tophead) minmax(auto, var(--aml-player-height))
      1fr;
  }
  &[data-source='lightbox'] .wt-asset-editor-middle {
    grid-template-columns: minmax(20px, auto) minmax(auto, 688px) minmax(
        20px,
        auto
      );
    grid-template-rows: 30px minmax(auto, 387px) 1fr;
  }
  &[data-source='timeline'] .wt-asset-editor-middle {
    grid-template-columns: minmax(20px, auto) minmax(auto, 688px) minmax(
        20px,
        auto
      );
    grid-template-rows: 110px minmax(auto, 387px) 1fr;
  }
}
</style>
